import { mapGettersShorthand } from '@/store/utils/mappers';
import WidgetService from '@/utils/WidgetService';
import AppService from '@/utils/AppService';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import {
	editWorkflow,
	getNodeByUuidForSelectedWorkflowVersion,
	isRootNodeOfSubFlow,
	rootNode as selectedWorkflowRootNode,
} from '@/store/modules/selectedWorkflow/getters';
import { getWidget, getWidgetFieldData } from '@/store/modules/widgets/getters';
import { getService } from '@/store/modules/apps/getters';
import ParamsService from '@/utils/ParamsService';
import { shouldFormatPropertyKey } from '@/store/modules/auth/getters';
import { getAction } from '@/store/modules/actions/getters';
import { getSubflowParams } from '@/store/modules/subflows/getters';
import { rootNode, getWorkflow, getWorkflowDraftVersion, getWorkflowCurrentVersion } from '@/store/modules/workflows/getters';
import { getConfigurationAndInterfaceIdByFieldUUID } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import {
	GENERAL_CONFIGURATIONS_FIELDS_KEY,
	USER_CONFIGURATIONS_FIELDS_KEY,
	VIRTUAL_FIELDS,
	CONFIGURATIONS_FIELDS_KEY,
	KEY_TITLE_MAPPER,
} from '@/utils/workflow-node.const';
import { isUUID } from '@/utils/helpers';
import { subflowIdFromNode } from '@/store/modules/subflows/getters';

export default {
	name: 'DataInputMenuItemBaseMixin',
	computed: {
		...mapGettersShorthand({
			editWorkflow,
			getNodeByUuidForSelectedWorkflowVersion,
			getWidget,
			shouldFormatPropertyKey,
			getService,
			getAction,
			rootNode,
			getWidgetFieldData,
			getConfigurationAndInterfaceIdByFieldUUID,
			getWorkflowDraftVersion,
			getWorkflowCurrentVersion,
			getSubflowParams,
			getWorkflow,
			subflowIdFromNode,
			isRootNodeOfSubFlow,
			selectedWorkflowRootNode,
		}),
	},
	methods: {
		getNodeData(nodeUUID, workflowId = null) {
			const nodeData = VIRTUAL_FIELDS.includes(nodeUUID)
				? workflowId
					? this.rootNode(workflowId)
					: this.selectedWorkflowRootNode
				: this.getNodeByUuidForSelectedWorkflowVersion(nodeUUID);
			return nodeData;
		},
		getItemKeyTitle(nodeUUID, key, workflowId = null) {
			const nodeData = this.getNodeData(nodeUUID, workflowId);
			const service = this.getService(nodeData?.service);
			const isWidget = AppService.isSlugOfWidget(service?.service_slug);
			const isBranches = AppService.isSlugOfBranches(service?.service_slug);
			const isSubFlow = AppService.isSlugOfSubflow(service?.service_slug);
			const isRootNodeOfSubFlow = nodeData && this.isRootNodeOfSubFlow(nodeData);
			const testResponseOutputLabels = WorkflowNodeService.getTestResponseOutputLabels(nodeData);
			const outputLabel = WorkflowNodeService.getOutputLabelForKey(testResponseOutputLabels, key);
			let keyTitle = ParamsService.formatPropertyKeyToReadable(outputLabel, this.shouldFormatPropertyKey);

			const changeKey = Object.keys(KEY_TITLE_MAPPER).find((x) => keyTitle == x);
			if (changeKey) {
				keyTitle = keyTitle.replace(changeKey, KEY_TITLE_MAPPER[changeKey]);
			} else if (isWidget) {
				const widgetId = WorkflowNodeService.getNodeWidgetId(nodeData);
				const widget = this.getWidget(widgetId);
				if (widget) {
					keyTitle = WidgetService.replaceKeyWithIdentifier(widget, key);
				}
			} else if (isBranches) {
				const isConsolidateDataNode = WorkflowNodeService.isConsolidateDataNode(nodeData, this.getAction);
				if (isConsolidateDataNode) {
					keyTitle = WorkflowNodeService.getConsolidateDataParamName(nodeData, key);
				}
			} else if (
				[GENERAL_CONFIGURATIONS_FIELDS_KEY, USER_CONFIGURATIONS_FIELDS_KEY].includes(nodeUUID) ||
				key.startsWith(CONFIGURATIONS_FIELDS_KEY)
			) {
				let configurationKey = key;

				const PREFIX = `${CONFIGURATIONS_FIELDS_KEY}.`;
				if (key.startsWith(PREFIX)) {
					// example: configuration.1ff49331-c46d-46fe-b6ec-d8aafa9e44a7.params_map.app_id
					configurationKey = key.replace(PREFIX, '');
				}

				// example: 1ff49331-c46d-46fe-b6ec-d8aafa9e44a7.params_map.app_id
				const [configurationFieldUUID, ...keySuffixParts] = configurationKey?.split('.');
				const configurationInterfaceUUID = this.getConfigurationAndInterfaceIdByFieldUUID(configurationFieldUUID)?.interfaceUUID;
				const widget = this.getWidget(configurationInterfaceUUID);
				if (widget) {
					keyTitle = WidgetService.replaceKeyWithIdentifier(widget, configurationFieldUUID, {
						keySuffixParts,
						getService: this.getService,
					});
				}
			} else if (isRootNodeOfSubFlow && isUUID(keyTitle)) {
				const workflowDraftVersion = this.editWorkflow.current_draft;
				const subFlowInputConfigurationId = workflowDraftVersion.sub_flow_input_configuration;
				const subFlowConfigurationParams = this.getSubflowParams(subFlowInputConfigurationId) || [];
				const paramForKey = subFlowConfigurationParams.find((x) => String(x.uuid) === String(key));
				if (paramForKey?.name && keyTitle !== paramForKey.name) {
					keyTitle = paramForKey.name;
				}
			} else if (isSubFlow && isUUID(keyTitle)) {
				const subFlowId = this.subflowIdFromNode(nodeData);
				const workflowCurrentVersion = this.getWorkflowCurrentVersion(Number(subFlowId));
				if (workflowCurrentVersion) {
					const subFlowOutputConfigurationId = workflowCurrentVersion.sub_flow_output_configuration;
					const subFlowConfigurationParams = this.getSubflowParams(subFlowOutputConfigurationId) || [];
					const paramForKey = subFlowConfigurationParams.find((x) => String(x.uuid) === String(key));
					if (paramForKey?.name && keyTitle !== paramForKey.name) {
						keyTitle = paramForKey.name;
					}
				}
			}
			return keyTitle;
		},
		widgetFieldData(fieldUUID) {
			return this.getWidgetFieldData(fieldUUID);
		},
	},
};
